// @ts-check
// Assistente da campanha de e-mail em rascunho (spec/email.md seções 10.3 e 19.7; contrato 09 de e-mail, seção 10):
// barra de passos (Público, Remetente e assunto, Conteúdo, Revisão) com o ponto de cada passo pela última revisão,
// gravação automática com conflito pela hora da última gravação, rodapé fixo com "Salvo", voltar, avançar e, na revisão,
// "Enviar teste", "Agendar" e "Enviar agora". O público usa o AudienceBuilder (filtros do lead) e o conteúdo o editor
// dos modelos; enviar e agendar congelam o conteúdo no servidor.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { searchParamsState, updateSearchParams } from '../../app/router.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { reviewEmailCampaign, sendEmailCampaign, updateEmailCampaign } from '../../data/email.js';
import { createEmptyAudienceDefinition } from '../../../../shared/audiences/audience-definition.js';
import { EMAIL_SENDING_PROTECTION } from '../../../../shared/email/email-settings.js';
import { WIZARD_STEPS, readWizardStep, wizardStepStates } from '../../lib/email-campaigns.js';
import { formatTime } from '../../lib/format.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { useIsMobile } from '../../ui/hooks.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { AudienceBuilder } from '../audiences/audience-builder.js';
import { useEmailHistory } from '../email-design/email-document-store.js';
import { EmailPreviewDialog } from '../email-design/email-preview-frame.js';
import { useLocalEmailPreview } from '../email-design/email-content-editor.js';
import { useEmailBranding } from '../email-design/email-branding.js';
import { saveErrorMessage } from './email-template-page.js';
import { ContentStep } from './campaign-steps/content-step.js';
import { ReviewStep } from './campaign-steps/review-step.js';
import { SenderStep } from './campaign-steps/sender-step.js';
import { EmailCampaignScheduleDialog } from './campaign-schedule-dialog.js';
import { EmailCampaignTestDialog } from './campaign-test-dialog.js';
import { viewsState } from '../../data/metadata-store.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email.js').EmailCampaign} EmailCampaign
 * @typedef {import('../../data/email.js').CampaignReview} CampaignReview
 * @typedef {import('../../lib/email-campaigns.js').WizardStep} WizardStep
 * @typedef {import('./campaign-steps/content-step.js').CampaignContent} CampaignContent
 * @typedef {{ name: string, audienceDefinition: any, senderType: 'INSTITUTION' | 'CONNECTED_ACCOUNT', connectedAccountId: string | null,
 *   fromName: string, utmEnabled: boolean, mediaCampaignId: string | null }} CampaignSettings
 */

const SAVE_DELAY_MILLISECONDS = 1200;
const STEP_ICONS = Object.freeze({ audience: 'IconUsers', sender: 'IconAt', content: 'IconMail', review: 'IconChecklist' });

/** @param {EmailCampaign} campaign @returns {CampaignSettings} */
export const settingsOf = (campaign) => ({
  name: campaign.name, audienceDefinition: campaign.audienceDefinition ?? createEmptyAudienceDefinition({ usage: 'AUDIENCE' }),
  senderType: campaign.senderType, connectedAccountId: campaign.connectedAccountId, fromName: campaign.fromName ?? '', utmEnabled: campaign.utmEnabled,
  mediaCampaignId: campaign.mediaCampaignId,
});

/** @param {EmailCampaign} campaign @returns {CampaignContent} */
export const contentOf = (campaign) => ({
  editorMode: campaign.editorMode, document: campaign.document ?? null, subject: campaign.subject, preheader: campaign.preheader, purpose: 'MARKETING',
});

/**
 * O que mudou desde a última gravação (o PATCH leva só isso).
 * @param {{ settings: CampaignSettings, content: CampaignContent }} saved @param {{ settings: CampaignSettings, content: CampaignContent }} current
 */
export function campaignPatch(saved, current) {
  /** @type {Record<string, unknown>} */
  const patch = {};
  for (const key of /** @type {Array<keyof CampaignSettings>} */ (Object.keys(current.settings))) {
    if (JSON.stringify(current.settings[key]) !== JSON.stringify(saved.settings[key])) patch[key] = current.settings[key];
  }
  if ('name' in patch) patch.name = String(patch.name ?? '').trim() || saved.settings.name;
  if ('fromName' in patch) patch.fromName = String(patch.fromName ?? '').trim() || null;
  if (current.content.subject !== saved.content.subject) patch.subject = current.content.subject;
  if (current.content.preheader !== saved.content.preheader) patch.preheader = current.content.preheader;
  if (current.content.document !== saved.content.document) patch.document = current.content.document;
  return patch;
}

/** Aviso da atualização de um rascunho da 1.13.0 (spec/email.md seção 18.2). @param {{ upgrade: { notes: string[], legacy: any }, onDismiss: () => void }} props */
function LegacyUpgradeNotice({ upgrade, onDismiss }) {
  const filter = upgrade.legacy?.audience?.filter;
  const view = viewsState.value.find((item) => item.id === upgrade.legacy?.audience?.viewId);
  return html`<div class="ec-notice" data-tone="blue" role="status" data-testid="email-campaign-legacy-notice">
    <${Icon} name="IconInfoCircle" size=${16} />
    <div>
      <strong>${t('emailCampaigns.legacy.upgradedTitle')}</strong>
      <ul class="ec-notice-list">
        ${upgrade.notes.includes('CONTENT_CONVERTED') ? html`<li>${t('emailCampaigns.legacy.contentConverted')}</li>` : null}
        ${upgrade.notes.includes('AUDIENCE_FROM_VIEW') ? html`<li>${t('emailCampaigns.legacy.audienceFromView', { view: view ? translateMetadataLabel(view.name) : '' })}</li>` : null}
        ${upgrade.notes.includes('AUDIENCE_RESET') ? html`<li>${t('emailCampaigns.legacy.audienceReset')}</li>` : null}
        ${upgrade.notes.includes('AUDIENCE_RESET') && filter ? html`<li class="ec-mono">${t('emailCampaigns.legacy.oldFilter', { filter })}</li>` : null}
      </ul>
    </div>
    <${Button} title=${t('emailCampaigns.legacy.dismiss')} size="small" variant="secondary" onClick=${onDismiss} />
  </div>`;
}

/**
 * Prévia do rascunho: a do navegador na hora e, com um lead escolhido, a do servidor.
 * @param {{ content: CampaignContent, senderName: string, onClose: () => void }} props
 */
function CampaignPreviewDialog({ content, senderName, onClose }) {
  const branding = useEmailBranding();
  const local = useLocalEmailPreview(/** @type {any} */ (content), { settings: branding.settings, institutionName: branding.institutionName, logoUrl: branding.logoUrl });
  return html`<${EmailPreviewDialog} onClose=${onClose} local=${local} senderName=${senderName}
    request=${{ document: content.document, subject: content.subject, preheader: content.preheader, purpose: 'MARKETING' }} />`;
}

/**
 * @param {{ initialCampaign: EmailCampaign, upgrade?: { notes: string[], legacy: any } | null, onCampaignReplaced: (campaign: EmailCampaign) => void,
 *   onSaved?: (campaign: EmailCampaign) => void }} props
 */
export function EmailCampaignWizard({ initialCampaign, upgrade = null, onCampaignReplaced, onSaved }) {
  const isMobile = useIsMobile();
  const branding = useEmailBranding();
  const [campaign, setCampaign] = useState(initialCampaign);
  const [settings, setSettings] = useState(() => settingsOf(initialCampaign));
  const history = useEmailHistory(contentOf(initialCampaign));
  const content = history.value;
  const [saveState, setSaveState] = useState(/** @type {'IDLE' | 'PENDING' | 'SAVING' | 'SAVED' | 'FAILED'} */ ('IDLE'));
  const [savedAt, setSavedAt] = useState(/** @type {string | null} */ (null));
  const [conflict, setConflict] = useState(/** @type {EmailCampaign | null} */ (null));
  const [review, setReview] = useState(/** @type {CampaignReview | null} */ (null));
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewError, setReviewError] = useState(/** @type {string | null} */ (null));
  const [dialog, setDialog] = useState(/** @type {'TEST' | 'SCHEDULE' | 'SEND' | 'PREVIEW' | null} */ (null));
  const [isSending, setIsSending] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(Boolean(upgrade));
  // o AudienceBuilder guarda o próprio estado: trocar o público por fora (conflito) monta um novo
  const [audienceKey, setAudienceKey] = useState(0);
  const step = readWizardStep(searchParamsState.value.get('step'));
  const stepIndex = WIZARD_STEPS.indexOf(step);

  const savedRef = useRef({ settings: settingsOf(initialCampaign), content: contentOf(initialCampaign) });
  const currentRef = useRef({ settings, content });
  currentRef.current = { settings, content };
  const campaignRef = useRef(campaign);
  campaignRef.current = campaign;
  const timerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const savingRef = useRef(/** @type {Promise<boolean> | null} */ (null));
  const conflictRef = useRef(conflict);
  conflictRef.current = conflict;

  const isDirty = () => Object.keys(campaignPatch(savedRef.current, currentRef.current)).length > 0;

  /** Grava o que mudou; devolve se ficou tudo salvo. @returns {Promise<boolean>} */
  const save = async () => {
    if (conflictRef.current) return false;
    if (savingRef.current) await savingRef.current;
    const sent = currentRef.current;
    const patch = campaignPatch(savedRef.current, sent);
    if (Object.keys(patch).length === 0) return true;
    setSaveState('SAVING');
    const request = updateEmailCampaign(campaignRef.current.id, { ...patch, expectedUpdatedAt: campaignRef.current.updatedAt })
      .then((updated) => {
        savedRef.current = sent;
        setCampaign(updated);
        // o título da página acompanha o nome salvo
        onSaved?.(updated);
        setSavedAt(updated.updatedAt);
        const stillDirty = isDirty();
        setSaveState(stillDirty ? 'PENDING' : 'SAVED');
        return !stillDirty;
      })
      .catch((error) => {
        if (error instanceof ApiError && error.code === 'EDIT_CONFLICT' && error.details?.current) {
          setConflict(/** @type {EmailCampaign} */ (error.details.current));
          setSaveState('IDLE');
        } else {
          setSaveState('FAILED');
          const message = saveErrorMessage(error);
          if (message) enqueueSnackbar({ variant: 'error', message });
          else if (!(error instanceof ApiError) || error.statusCode !== 0) showErrorSnackbar(error);
        }
        return false;
      })
      .finally(() => {
        savingRef.current = null;
      });
    savingRef.current = request;
    const result = await request;
    if (result === false && !conflictRef.current) return false;
    if (isDirty()) scheduleSave();
    return result;
  };
  const scheduleSave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (isDirty()) save();
    }, SAVE_DELAY_MILLISECONDS);
  };
  /** Grava agora (antes de revisar, testar, enviar e sair). */
  const flush = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const ok = isDirty() ? await save() : true;
    if (!ok) throw new Error(t('emailCampaigns.wizard.saveFailed'));
    return ok;
  };

  useEffect(() => {
    if (conflict || !isDirty()) return;
    setSaveState((state) => (state === 'SAVING' ? state : 'PENDING'));
    scheduleSave();
  }, [settings, content]);

  useEffect(() => {
    /** @param {BeforeUnloadEvent} event */
    const warn = (event) => {
      if (!isDirty()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (isDirty()) save();
    };
  }, []);

  /** Campanha que veio do servidor por outra ação (modelo aplicado, conflito resolvido). @param {EmailCampaign} replaced */
  const adoptCampaign = (replaced) => {
    setCampaign(replaced);
    const next = { settings: settingsOf(replaced), content: contentOf(replaced) };
    savedRef.current = next;
    setSettings(next.settings);
    history.reset(next.content);
    setSaveState('SAVED');
    setSavedAt(replaced.updatedAt);
  };

  const runReview = async () => {
    setIsReviewing(true);
    setReviewError(null);
    try {
      await flush();
      setReview(await reviewEmailCampaign(campaignRef.current.id));
    } catch (error) {
      setReviewError(error instanceof ApiError ? getErrorMessage(error) : String(/** @type {any} */ (error)?.message ?? error));
    } finally {
      setIsReviewing(false);
    }
  };
  useEffect(() => {
    if (step === 'review') runReview();
  }, [step]);

  /** @param {WizardStep} next */
  const goToStep = (next) => {
    updateSearchParams({ step: next === 'audience' ? null : next });
    window.scrollTo({ top: 0 });
  };

  const sendNow = async () => {
    setIsSending(true);
    try {
      await flush();
      const started = await sendEmailCampaign(campaignRef.current.id);
      showSuccessSnackbar(t('emailCampaigns.toasts.sendStarted'));
      setDialog(null);
      updateSearchParams({ step: null });
      onCampaignReplaced(started);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CAMPAIGN_REVIEW_FAILED' && Array.isArray(error.details?.checks)) {
        setReview({ checks: error.details.checks, canSend: false, estimate: review?.estimate ?? null });
        setDialog(null);
        goToStep('review');
      }
      showErrorSnackbar(error);
    } finally {
      setIsSending(false);
    }
  };

  const stepStates = wizardStepStates(review?.checks ?? null);
  const response = /** @type {any} */ (branding.response);
  const replyTo = branding.settings?.replyToEmail || response?.replyToAddress || '';
  const returnPath = `${window.location.pathname}${window.location.search}`;

  const saveStatus = () => {
    if (saveState === 'SAVING') return html`<span class="ec-save-state" data-state="saving"><${Icon} name="IconRefresh" size=${14} className="ec-spin" />${t('emailCampaigns.wizard.saving')}</span>`;
    if (saveState === 'FAILED') {
      return html`<span class="ec-save-state" data-state="failed" role="alert">${t('emailCampaigns.wizard.saveFailed')}
        <button type="button" class="ec-link-button" onClick=${() => save()}>${t('emailCampaigns.wizard.retry')}</button></span>`;
    }
    if (saveState === 'PENDING') return html`<span class="ec-save-state" data-state="pending">${t('emailCampaigns.wizard.unsaved')}</span>`;
    if (savedAt) return html`<span class="ec-save-state" data-state="saved"><${Icon} name="IconCheck" size=${14} />${t('emailCampaigns.wizard.saved', { time: formatTime(savedAt) })}</span>`;
    return html`<span class="ec-save-state"></span>`;
  };

  return html`<div class="ec-wizard" data-step=${step} data-testid="email-campaign-wizard">
    ${isMobile
      ? html`<div class="ec-stepper-mobile" aria-live="polite">
          <span>${t('emailCampaigns.steps.progress', { current: stepIndex + 1, total: WIZARD_STEPS.length })}</span>
          <strong>${t(`emailCampaigns.steps.${step}`)}</strong>
        </div>`
      : html`<nav class="ec-stepper" aria-label=${t('emailCampaigns.title')}>
          ${WIZARD_STEPS.map((item, index) => html`<button key=${item} type="button" class="ec-stepper-item" aria-current=${item === step ? 'step' : undefined}
            data-state=${stepStates[item] ?? undefined} onClick=${() => goToStep(item)}>
            <span class="ec-stepper-index">${index + 1}</span>
            <${Icon} name=${STEP_ICONS[item]} size=${16} />
            <span>${t(`emailCampaigns.steps.${item}`)}</span>
            ${stepStates[item]
              ? html`<span class="ec-stepper-dot" data-state=${stepStates[item]} title=${t(stepStates[item] === 'ERROR' ? 'emailCampaigns.steps.hasErrors' : 'emailCampaigns.steps.isComplete')}></span>`
              : null}
          </button>`)}
        </nav>`}
    ${showUpgrade && upgrade ? html`<${LegacyUpgradeNotice} upgrade=${upgrade} onDismiss=${() => setShowUpgrade(false)} />` : null}
    ${conflict
      ? html`<div class="ec-notice" data-tone="orange" role="alert">
          <${Icon} name="IconAlertTriangle" size=${16} />
          <span>${t('emailCampaigns.wizard.conflict')}</span>
          <div class="ec-button-row">
            <${Button} title=${t('emailCampaigns.wizard.conflictReload')} size="small" variant="secondary" onClick=${() => {
              adoptCampaign(conflict);
              setAudienceKey((key) => key + 1);
              setConflict(null);
            }} />
            <${Button} title=${t('emailCampaigns.wizard.conflictKeepMine')} size="small" variant="secondary" onClick=${() => {
              // a minha versão vale: o próximo PATCH leva a data da gravada
              setCampaign((current) => ({ ...current, updatedAt: conflict.updatedAt }));
              campaignRef.current = { ...campaignRef.current, updatedAt: conflict.updatedAt };
              savedRef.current = { settings: settingsOf(conflict), content: contentOf(conflict) };
              setConflict(null);
              conflictRef.current = null;
              requestAnimationFrame(() => save());
            }} />
          </div>
        </div>`
      : null}

    <div class="ec-wizard-body">
      ${step === 'audience'
        ? html`<div class="ec-step" data-testid="email-campaign-audience-step">
            <p class="ec-muted">${t('emailCampaigns.audience.hint')}</p>
            <${AudienceBuilder} key=${audienceKey} value=${settings.audienceDefinition} channels=${['EMAIL']} purpose="MARKETING" usage="AUDIENCE" showRecipients=${true}
              title=${t('emailCampaigns.audience.title')}
              onChange=${(/** @type {any} */ definition) => setSettings((current) => ({ ...current, audienceDefinition: definition }))} />
          </div>`
        : null}
      ${step === 'sender'
        ? html`<${SenderStep} value=${settings} subject=${content.subject} preheader=${content.preheader}
            sendingAddress=${response?.sendingAddress ?? ''} replyToAddress=${replyTo}
            onChange=${(/** @type {Partial<CampaignSettings>} */ patch) => setSettings((current) => ({ ...current, ...patch }))}
            onLinesChange=${(/** @type {{ subject?: string, preheader?: string }} */ patch) => history.set({ ...content, ...patch }, { coalesceKey: Object.keys(patch)[0] })} />`
        : null}
      ${step === 'content'
        ? html`<${ContentStep} campaign=${campaign} content=${content.document ? content : null} returnPath=${returnPath}
            templateChanged=${Boolean(review?.checks.some((check) => check.code === 'TEMPLATE_CHANGED'))}
            onContentChange=${(/** @type {CampaignContent} */ value, /** @type {{ coalesceKey?: string } | undefined} */ options) => history.set(value, options)}
            onStartContent=${(/** @type {CampaignContent} */ started) => history.set(started)}
            onReplaced=${(/** @type {EmailCampaign} */ replaced) => adoptCampaign(replaced)}
            onPreview=${() => setDialog('PREVIEW')} onTest=${() => setDialog('TEST')} />`
        : null}
      ${step === 'review'
        ? html`<${ReviewStep} review=${review} isLoading=${isReviewing} error=${reviewError} onRefresh=${runReview} onGoToStep=${goToStep} />`
        : null}
    </div>

    <footer class="ec-wizard-footer">
      ${saveStatus()}
      <div class="ec-button-row">
        ${stepIndex > 0 ? html`<${Button} title=${t('emailCampaigns.wizard.back')} icon="IconChevronLeft" size="small" variant="secondary" onClick=${() => goToStep(WIZARD_STEPS[stepIndex - 1])} />` : null}
        ${step === 'review'
          ? html`<${Button} title=${t('emailCampaigns.actions.sendTest')} icon="IconSend" size="small" variant="secondary" onClick=${() => setDialog('TEST')} />
              <${Button} title=${t('emailCampaigns.send.schedule')} icon="IconCalendarTime" size="small" variant="secondary" disabled=${!review?.canSend}
                onClick=${() => setDialog('SCHEDULE')} data-testid="email-campaign-schedule-open" />
              <${Button} title=${t('emailCampaigns.send.now')} icon="IconSend" size="small" accent="blue" disabled=${!review?.canSend}
                onClick=${() => setDialog('SEND')} data-testid="email-campaign-send-now" />`
          : html`<${Button} title=${t('emailCampaigns.wizard.next')} icon="IconChevronRight" size="small" accent="blue" onClick=${() => goToStep(WIZARD_STEPS[stepIndex + 1])}
              data-testid="email-campaign-next" />`}
      </div>
    </footer>

    ${dialog === 'TEST'
      ? html`<${EmailCampaignTestDialog} campaignId=${campaign.id} campaignName=${campaign.name} beforeSend=${flush} onClose=${() => setDialog(null)}
          onSent=${() => {
            if (step === 'review') runReview();
          }} />`
      : null}
    ${dialog === 'SCHEDULE'
      ? html`<${EmailCampaignScheduleDialog} campaignId=${campaign.id} beforeSchedule=${flush} onClose=${() => setDialog(null)}
          onScheduled=${(/** @type {EmailCampaign} */ scheduled) => {
            setDialog(null);
            updateSearchParams({ step: null });
            onCampaignReplaced(scheduled);
          }} />`
      : null}
    ${dialog === 'SEND'
      ? html`<${Modal} title=${t('emailCampaigns.send.confirmTitle')}
          subtitle=${t('emailCampaigns.send.confirmDescription', { count: review?.estimate?.recipients ?? 0 })}
          confirmLabel=${t('emailCampaigns.send.confirmSend')} isLoading=${isSending} onClose=${() => setDialog(null)} onConfirm=${sendNow}>
          ${(review?.estimate?.recipients ?? 0) >= EMAIL_SENDING_PROTECTION.warmupMinimumEligible
            ? html`<p class="input-hint">${t('emailCampaigns.send.warmupNote')}</p>`
            : null}
        <//>`
      : null}
    ${dialog === 'PREVIEW' && content.document
      ? html`<${CampaignPreviewDialog} content=${content} senderName=${settings.fromName || branding.settings?.fromName || branding.institutionName}
          onClose=${() => setDialog(null)} />`
      : null}
  </div>`;
}
